import {Box, Stack} from '@mui/material';
import React, {Fragment, Suspense, useCallback, useEffect} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {useIsMutating} from '@tanstack/react-query';
import {MUTATION_KEYS} from 'infra/libs/react-query/mutationKeys';
import {MainFooter, MainHeader} from 'presentation/components';
import {DiscardChangesModal} from 'presentation/components/ChangeData/DiscardChangesModal';
import {ErrorLoadingData} from 'presentation/components/ChangeData/ErrorLoadingData';
import {useSubscriptionDialogsStateStore} from 'presentation/components/SubscriptionDialogs/store/useSubscriptionDialogsStateStore';
import {FeaturesEnum, HomeEnum} from 'presentation/constants';
import {useChangeDataProfile, useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {ErrorBoundary} from 'react-error-boundary';
import {ProfileContainer} from './styles';

import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {ConfirmationOtpModal} from '../components/ConfirmationOtpModal';
import {useProfileSectionStore} from '../store/useProfileSection';
import {PROFILE_SECTION} from '../store/useProfileSection/constants';
import {ProfileContent} from './ProfileContent';
import {ProfileContentLegacy} from './ProfileContentLegacy';

const {title} = translations['pt-br'].privateFlow.profile;
const {registerChange, loginAndPrivacy} = translations['pt-br'].others.profileMenu;

export function Profile() {
	const navigate = useNavigate();
	const {state: routeState} = useLocation();
	const {isMobile} = useMediaQueries();

	const {isEnabled: isNewProfileFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.ADD__PORTAL_FEATURE_NEW_PROFILE,
	);

	const isMutatingEmailToken = useIsMutating({mutationKey: [MUTATION_KEYS.CHANGE_EMAIL]}) > 0;
	const isMutatingPhoneToken = useIsMutating({mutationKey: [MUTATION_KEYS.CHANGE_PHONE]}) > 0;
	const activeSection = useProfileSectionStore(state => state.activeSection);

	const {
		resetChangeDataFormState,
		isModalDiscardChangesOpenState,
		handleAbandonOtpModal,
		handleOnConfirmDiscardChangesModal,
		handleResendOtpCode,
		handleToggleOtpModal,
		isModalOtpConfirmationOpen,
		setIsModalDiscardChangesOpenState,
	} = useChangeDataProfile();

	const setDialogs = useSubscriptionDialogsStateStore(state => state.updateState);
	const dialogs = useSubscriptionDialogsStateStore(state => state.state);

	if (dialogs.isSubscriptionModalOpen) {
		setDialogs({
			isSubscriptionModalOpen: false,
		});
	}

	const handleBack = useCallback(() => {
		if (routeState) {
			navigate(routeState);
			return;
		}
		navigate(HomeEnum.HOME_PAGE);
	}, [routeState]);

	useEffect(() => {
		resetChangeDataFormState();
	}, []);

	const sectionTitles = {
		[PROFILE_SECTION.REGISTRATION_DATA]: registerChange,
		[PROFILE_SECTION.LOGIN_AND_PRIVACY]: loginAndPrivacy,
	};

	const getPageTitle = (): string => {
		if (isMobile && isNewProfileFeatureFlagEnabled) {
			return sectionTitles[activeSection] ?? title;
		}

		return title;
	};

	return (
		<Fragment>
			<MainHeader title={getPageTitle()} handleBack={handleBack} hideProfileMenu />

			<Stack justifyContent={isNewProfileFeatureFlagEnabled ? 'start' : 'center'} flex={1}>
				<ErrorBoundary
					fallbackRender={({resetErrorBoundary}) => (
						<ErrorLoadingData resetErrorBoundary={resetErrorBoundary} />
					)}
					onReset={() => window.location.reload()}>
					<ProfileContainer>
						{isNewProfileFeatureFlagEnabled ? (
							<ProfileContent />
						) : (
							<ProfileContentLegacy />
						)}
					</ProfileContainer>

					<DiscardChangesModal
						open={isModalDiscardChangesOpenState}
						setOpen={setIsModalDiscardChangesOpenState}
						onConfirm={handleOnConfirmDiscardChangesModal}
					/>

					{isModalOtpConfirmationOpen.open ? (
						<Suspense>
							<ConfirmationOtpModal
								open={isModalOtpConfirmationOpen.open}
								setOpen={handleToggleOtpModal}
								type={isModalOtpConfirmationOpen.type}
								data={isModalOtpConfirmationOpen.data}
								handleAbandon={handleAbandonOtpModal}
								onResend={handleResendOtpCode}
								loading={isMutatingEmailToken || isMutatingPhoneToken}
							/>
						</Suspense>
					) : null}
				</ErrorBoundary>
			</Stack>

			<Box mt="auto">
				<MainFooter />
			</Box>
		</Fragment>
	);
}
